import {
  FunctionField,
  DateField,
  DateInput as RaDateInput,
  TimeInput as RaTimeInput,
  useInput,
  useListContext,
  useResourceContext,
  useTranslate,
  useRecordContext,
  Translate
} from 'react-admin'
import { useFormContext, useWatch } from 'react-hook-form'
import React from 'react'
import {
  Box,
  Button,
  Fab,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  styled,
  Select,
  SelectChangeEvent,
  TextField,
  Toolbar,
  useScrollTrigger
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import {
  find,
  flow,
  isNil,
  isString,
  map,
  replace,
  split,
  startsWith,
  toNumber,
  toString
} from 'lodash/fp'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import ja from 'date-fns/locale/ja'
import { format, parse, startOfMonth, isValid } from 'date-fns/fp'
import log from 'loglevel'
import { useNavigate } from 'react-router-dom'

import customLogos from './customLogos.json'

const StyledFab = styled(Fab)({
  zIndex: 1,
  margin: '0 auto'
})

log.setLevel(log.levels.DEBUG)

export const ScrollTopId = 'back-to-top-anchor'
export const ScrollTop: React.FC = () => {
  const translate = useTranslate()
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100
  })
  // @ts-expect-error
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const anchor = document.querySelector(`#${ScrollTopId}`)
    if (!isNil(anchor)) {
      anchor.scrollIntoView({
        block: 'center'
      })
    }
  }
  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}>
        <StyledFab
          color="primary"
          size="small"
          aria-label={translate('mine.labels.scrollTop')}>
          <KeyboardArrowUpIcon />
        </StyledFab>
      </Box>
    </Fade>
  )
}
export const ScrollBottomId = 'forward-to-bottom-anchor'
export const ScrollBottom: React.FC = () => {
  const translate = useTranslate()
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100
  })
  // @ts-expect-error
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const anchor = document.querySelector(`#${ScrollBottomId}`)
    if (!isNil(anchor)) {
      anchor.scrollIntoView({
        block: 'center'
      })
    }
  }
  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', top: 64, right: 16 }}>
        <StyledFab
          color="primary"
          size="small"
          aria-label={translate('mine.labels.scrollBottom')}>
          <KeyboardArrowDownIcon />
        </StyledFab>
      </Box>
    </Fade>
  )
}
export const TimeInput: React.FC<{
  source: string
  validate: any
  disabled?: boolean
}> = ({ source, disabled, ...props }) => (
  <RaTimeInput
    {...props}
    disabled={!isNil(disabled) ? disabled : false}
    source={source}
    format={(value) => value}
    parse={(value) => value}
    fullWidth
  />
)
export const DateInput: React.FC<{
  source: string
  validate?: any
  disabled?: boolean
}> = ({ source, ...props }) => (
  <RaDateInput
    {...props}
    source={source}
    format={(value) => value}
    parse={(value) => value}
    fullWidth
  />
)
export const MonthInput: React.FC<{
  source: string
  disableFuture?: boolean
  disablePast?: boolean
  alwaysOn?: boolean
  validate?: any
}> = ({ source, disableFuture, disablePast, alwaysOn, ...props }) => {
  const translate = useTranslate()
  const currentValue = useWatch({ name: source })
  const [dateValue, setDateValue] = React.useState<Date | null>(currentValue)
  const { setValue } = useFormContext()
  const { field, isRequired } = useInput({ source, ...props })
  const resource = useResourceContext()
  const label = translate(
    `resources.${resource}.fields.${replace(/\.[0-9]+/g, '')(field.name)}`
  )
  const setDate = (e: any): void => {
    const date = parse(new Date(), 'yyyy年M月d日')(e.target.value)
    if (isValid(date)) {
      const value = format('yyyy-MM-dd')(date)
      setValue(source, value)
      field.onChange({
        target: {
          value
        }
      })
    }
  }
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <DatePicker
        label={label}
        value={dateValue}
        onChange={(newValue: Date | null): void => {
          if (isValid(newValue)) {
            const value = !isNil(newValue)
              ? format('yyyy-MM-dd')(startOfMonth(newValue))
              : ''
            setDateValue(newValue)
            setValue(source, value)
            field.onChange({ target: { value } })
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...field}
            onChange={setDate}
            required={isRequired}
            fullWidth
          />
        )}
        views={['year', 'month']}
        minDate={parse(new Date(), 'yyyy-MM-dd')('2023-01-01')}
        disableFuture={!isNil(disableFuture) ? disableFuture : false}
        disablePast={!isNil(disablePast) ? disablePast : false}
        {...props}
      />
    </LocalizationProvider>
  )
}
export const DayMonthInput: React.FC<{
  source: string
  disableFuture?: boolean
  disablePast?: boolean
  alwaysOn?: boolean
  validate?: any
}> = ({ source, ...props }) => {
  const translate = useTranslate()
  const currentValue = useWatch({ name: source })
  const [dateValue, setDateValue] = React.useState<Date | null>(
    isNil(currentValue) ? null : parse(new Date(), 'MM-dd')(currentValue)
  )
  const { setValue } = useFormContext()
  const { field, isRequired } = useInput({ source, ...props })
  const resource = useResourceContext()
  const label = translate(
    `resources.${resource}.fields.${replace(/\.[0-9]+/g, '')(field.name)}`
  )
  const setDate = (e: any): void => {
    const date = parse(new Date(), 'M月d日')(e.target.value)
    if (isValid(date)) {
      const value = format('MM-dd')(date)
      setValue(source, value)
      field.onChange({
        target: {
          value
        }
      })
    }
  }
  React.useEffect(() => {
    if (!isNil(currentValue)) {
      setDateValue(parse(new Date(), 'MM-dd')(currentValue))
    }
  }, [currentValue])
  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={ja}
      dateFormats={{ monthAndYear: 'M月' }}>
      <DatePicker
        label={label}
        value={dateValue}
        onChange={(newValue: Date | null): void => {
          if (isValid(newValue)) {
            const value = !isNil(newValue) ? format('MM-dd')(newValue) : ''
            setDateValue(newValue)
            setValue(source, value)
            field.onChange({
              target: {
                value
              }
            })
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            {...field}
            onChange={setDate}
            required={isRequired}
            fullWidth
          />
        )}
        views={['month', 'day']}
        inputFormat="M月d日"
        mask="__月__日"
        {...props}
      />
    </LocalizationProvider>
  )
}
export const MultilineTextField: React.FC<{
  source: string
}> = ({ source }) => (
  <FunctionField
    source={source}
    render={(record: Record<string, string>) =>
      flow([split('\n'), map((item: string) => <div>{item}</div>)])(
        record[source]
      )
    }
  />
)
export const Pagination: React.FC<{
  withPerPage?: boolean
}> = ({ withPerPage }) => {
  const translate = useTranslate()
  const { page, perPage, hasPreviousPage, hasNextPage, setPage, setPerPage } =
    useListContext()

  const handleChange = (event: SelectChangeEvent): void => {
    setPerPage(toNumber(event.target.value))
    setPage(0)
  }
  return (
    <>
      <Toolbar>
        {hasPreviousPage && (
          <Button
            key="previous"
            onClick={() => {
              setPage(page - 1)
            }}
            startIcon={<ChevronLeft />}>
            {translate('mine.pagination.previous')}
          </Button>
        )}
        {hasNextPage && (
          <Button
            key="next"
            onClick={() => {
              setPage(page + 1)
            }}
            startIcon={<ChevronRight />}>
            {translate('mine.pagination.next')}
          </Button>
        )}
      </Toolbar>
      {!isNil(withPerPage) && withPerPage && (
        <Toolbar>
          <FormControl
            sx={{
              m: 1,
              minWidth: 150
            }}
            size="small">
            <InputLabel id="pagination-per-page">
              {translate('mine.pagination.perPage')}
            </InputLabel>
            <Select
              labelId="pagtination-per-page"
              value={toString(perPage)}
              label={translate('mine.pagination.perPage')}
              onChange={handleChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      )}
    </>
  )
}
export const MyDateField: React.FC<{
  source: string
}> = ({ source, ...props }) => {
  const record = useRecordContext()
  if (isString(record[source as keyof typeof record])) {
    const date = parse(
      new Date(),
      'yyyy-MM-dd'
    )(record[source as keyof typeof record])
    record[source as keyof typeof record] = date
  }
  return (
    <DateField
      source={source}
      options={{
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }}
      {...props}
    />
  )
}
export const MyTimeField: React.FC<{
  source: string
}> = ({ source, ...props }) => {
  const record = useRecordContext()
  if (isString(record[source as keyof typeof record])) {
    const date = parse(
      new Date(),
      'HH:mm'
    )(record[source as keyof typeof record])
    record[source as keyof typeof record] = date
  }
  return (
    <DateField
      source={source}
      showTime
      showDate={false}
      options={{
        hour: '2-digit',
        minute: '2-digit'
      }}
      {...props}
    />
  )
}
export const Title: React.FC<{
  title: string
}> = ({ title }) => {
  const translate = useTranslate()
  // const redirect = useRedirect()
  // const record = useRecordContext()
  const navigate = useNavigate()
  const back = (): void => {
    // redirect(`/${record.id}`)
    navigate(-1)
  }
  return (
    <>
      <IconButton color="inherit" onClick={back}>
        <ArrowBackIcon />
      </IconButton>
      <span>{translate(title)}</span>
    </>
  )
}
export const HeaderLogo: React.FC<{ partner?: string | null | undefined }> = ({
  partner
}) => {
  if (isNil(partner)) {
    return (
      <picture>
        <img src="/siteLogoR.svg" width="80%" alt="terashite R" />
      </picture>
    )
  }
  const customLogo = find((item: string) => startsWith(`${partner}.`)(item))(
    customLogos
  )
  return isNil(customLogo) ? (
    <picture>
      <img src="/siteLogoR.svg" width="80%" alt="terashite R" />
    </picture>
  ) : (
    <picture>
      <img src={`/${customLogo}`} width="80%" alt="terashite R" />
    </picture>
  )
}
export const setTitle = (partner: string, translate: Translate): void => {
  const customTitle = translate(`resources.title.${partner}`)
  document.title =
    customTitle === `resources.title.${partner}`
      ? translate('resources.title')
      : customTitle
}
